/**
 * Terms of Service Page (Terms of Use)
 * Public legal page – StarClinch Terms of Use
 * iOS-friendly, consistent with ContactUsPage, FAQPage
 *
 * @author Varun Pandey
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Lock } from 'lucide-react';
import { motion } from 'framer-motion';

const LINK_CLASS =
  'text-[#F33363] underline underline-offset-2 decoration-[#F33363]/60 active:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1C1E] rounded px-0.5 -mx-0.5';

const SECTION_STAGGER = 0.05;

export interface TermsOfServicePageProps {
  /**
   * When rendering inside an overlay/sheet, we don't want a full-screen background
   * wrapper (the sheet already provides its own backdrop).
   */
  inSheet?: boolean;
}

export const TermsOfServicePage: React.FC<TermsOfServicePageProps> = ({
  inSheet = false,
}) => {
  const navigate = useNavigate();

  return (
    <div
      className={
        inSheet ? 'w-full' : 'w-full min-h-screen bg-[#0B0B0D] text-white'
      }
    >
      <div
        className={
          inSheet
            ? 'w-full max-w-3xl md:px-0 md:py-0 lg:py-0 pb-0'
            : 'w-full max-w-3xl mx-auto px-4 md:px-4 md:py-6 lg:py-8 pb-12'
        }
      >
        {/* Header – same pattern as Contact/FAQ */}
        <header className="my-6 lg:mb-8">
          {!inSheet && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 min-h-[44px] -ml-2 pl-2 pr-3 rounded-xl text-[#8E8E93] hover:text-white hover:bg-[#1C1C1E] active:bg-[#2C2C2E] transition-colors mb-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
          )}

          {inSheet ? <div className="flex flex-col items-center text-center">
            <div className="mt-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#4B0017]/60 border border-[#F33363]/35">
              {/* <Lock className="h-5 w-5 text-[#F33363]" /> */}
              <FileText className="h-5 w-5 text-[#F33363]" />
            </div>

            <h2 className="mt-4 text-xl font-bold text-white sm:text-2xl">
              Terms of Use
            </h2>

          </div> : <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
              <FileText className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                Terms of Use
              </h1>
              <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                StarClinch website terms and conditions
              </p>
            </div>
          </div>}

        </header>

        <div className="space-y-5 lg:space-y-6">
          {/* Intro section */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-5 lg:p-6"
          >
            <div className="space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The website{' '}
                <a
                  href="https://starclinch.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASS}
                >
                  https://starclinch.com/
                </a>{' '}
                (the &quot;Website&quot;) developed, owned and operated by VINSM Globe Pvt Ltd.
                (&quot;Company&quot; / &quot;StarClinch&quot;) is offered to its users, who may
                view/access the Website.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                By accessing and/or using the Website, you agree (1) to be bound by these Terms of
                Use and are deemed to have read and understood all of its contents and
                implications; and (2) you grant your free and unconditional consent to be subject
                to these Terms of Use as applicable.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The Terms of Use set forth herein apply to the Website and are covered and
                protected by all laws including but not limited to copyright and trademark laws of
                India.
              </p>
            </div>
          </motion.section>

          {/* Definitions */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 1, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Definitions
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                <strong className="font-semibold text-white">&quot;Express Booking&quot;</strong>{' '}
                refers to the instant-confirmation feature. The first Artist to accept an Express
                Booking offer is legally confirmed for the engagement and is bound to perform
                according to the stipulated terms.
              </p>
            </div>
          </motion.section>

          {/* StarClinch Services */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                StarClinch Services
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                StarClinch.com is provided to you to discover, explore and book entertainers
                through our featured content and interactive functionality available via the
                Internet (the &quot;StarClinch Services&quot;). The Terms of Use include our
                policy for acceptable use of the StarClinch Services and content, as well as your
                rights, obligations and restrictions regarding your use of the StarClinch
                Services. We grant you a limited, revocable, non-exclusive license to access the
                StarClinch Services for your own personal use, subject to the Terms of Use. Use
                of the StarClinch Services beyond the scope of authorized access granted to you
                terminates this permission.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                These terms and condition will constitute a binding contract between you and
                StarClinch. By accessing the StarClinch Services, you accept, without limitation
                or qualification, the following Terms of Use found and our existing StarClinch
                Rules (the &quot;Rules&quot;), found, which are incorporated by this reference. You
                are only authorized to use the StarClinch Services if you agree to abide by the
                Terms of Use and all applicable laws. If you do NOT accept the Terms of Use and
                the Rules, then please discontinue your use of the Site and the StarClinch
                Services. Your continued use of this website following the posting of any change
                or modification of these terms will mean you accept those changes or modifications.
                StarClinch reserves the right to withdraw access to this website and to change,
                modify, revise the materials, products and pricing listed or offered through this
                website at any time.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                We may modify the Terms of Use from time to time and each modification will be
                effective upon posting on the Site, which serves as notice to you. The Terms of
                Use constitute the entire agreement regarding your use of the Site or the
                StarClinch Services and supersede any such prior Terms of Use. We will attempt
                to notify all members of changes to these Terms of Use but are not obligated to
                do so, as it is important that you review the Terms of Use regularly to ensure
                you are updated as to any changes made.
              </p>
            </div>
          </motion.section>

          {/* Services or Fees */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Services or Fees
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                When you register as an Artist/Client, from time to time, StarClinch allows
                creation of the profile and access to StarClinch Services at such charges as
                provided in their engagement terms.
              </p>
              <p className="text-[15px] lg:text-base font-semibold text-white pt-1">
                Express Booking – Additional Terms
              </p>
              <ul className="list-none space-y-2 pl-0">
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    <strong className="font-semibold text-white">Binding Agreement:</strong> By
                    accepting an Express Booking, the Artist agrees to all specific Guidelines
                    associated with the feature.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    <strong className="font-semibold text-white">No Cancellation:</strong> Once accepted, the booking is non-cancellable by the Artist except in cases of documented safety risks.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    <strong className="font-semibold text-white">Performance Integrity: </strong> Repeated no-shows, lateness, or significant breaches of the performance standards will result in account suspension or reduced platform visibility.

                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    <strong className="font-semibold text-white">Exclusivity: </strong> All payments regarding the booking must be processed exclusively via StarClinch.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    <strong className="font-semibold text-white">Direct Dealing:</strong> Direct negotiation, contact exchange, or circumvention attempts with the Client for fees or future bookings outside the Platform is strictly prohibited.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    <strong className="font-semibold text-white">Platform Rights: </strong> StarClinch reserves the right to cancel bookings for safety, legal, non-compliance, or quality assurance reasons.
                  </span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Content on the Site */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Content on the Site
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You understand that all information, data, text, sound, photographs, graphics, video, messages, software, tags, advertisements or other materials (“Content”), whether publicly posted or privately transmitted, are the sole responsibility of the person from whom such Content originated. This means that you, and not StarClinch, are entirely responsible for all Content that you upload, post, transmit or otherwise make available through the StarClinch Services. We do not control the Content posted through the StarClinch Services and, as such, we do not guarantee the accuracy, integrity or quality of such Content and disclaim any and all liability in connection with such Content. The opinions expressed in postings or other Content provided through the StarClinch Services are not necessarily those of StarClinch or its owner, content providers, advertisers, sponsors, affiliated or related entities. You understand that by using the StarClinch Services you may be exposed to Content that is offensive, indecent or objectionable. You understand that we are not obligated to pre-screen, regularly review, monitor, delete or otherwise change the Content associated with the StarClinch Services. We do, however, reserve the right to do so at any time in our sole discretion, for any reason or no reason, and to delete any Content with or without notice. Under no circumstances will we be liable in any way for any Content, including, but not limited to, any errors or omissions in any Content, or any loss or damage of any kind incurred as a result of the use of any Content posted, emailed, transmitted or otherwise made available through the StarClinch Services.
              </p>
            </div>
          </motion.section>

          {/* Responsibility for Your Content (Artist Content) */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Responsibility for Your Content (Artist Content)
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You are solely responsible for the Content you submit or make available for inclusion through the StarClinch Services (“Artist Content“) and the consequences of posting or publishing it. In connection with Artist Content, you represent and warrant that you own or have the necessary licenses, rights, consents, and permissions to use and authorize StarClinch to use all trademark, copyright or other proprietary rights in and to any and all Artist Content to enable inclusion and use of the Artist Content in the manner intended by the StarClinch Services and the Terms of Use. You agree that you will not submit Artist Content that violates these Terms of Use.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Because StarClinch does not permit infringement of intellectual property rights through the StarClinch Services, we will remove all Artist Content if properly notified that such Site Content or Artist Content infringes on another’s intellectual property rights and at our sole discretion.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                StarClinch does not endorse any Artist Content or any opinion, recommendation, or advice expressed therein, and we expressly disclaim any and all liability in connection with Artist Content. You agree that you will not submit Artist Content that violates the Rules, found or material contrary to applicable local, national, and international laws and regulations.
              </p>
            </div>
          </motion.section>

          {/* Artist Content: Ownership & Licenses */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Artist Content: Ownership & Licenses
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                We do not claim ownership rights over your Artist Content. However, you grant us the following worldwide, royalty-free and non-exclusive license to use, distribute, reproduce, modify, adapt, publicly perform and publicly display Artist Content through the StarClinch Services, solely for the purpose for which such Artist Content was submitted or made available. This limited license exists only for so long as you elect to include Artist Content through the StarClinch Services and it will terminate at the time you remove or we remove such Artist Content from the StarClinch Services. You grant each user of the StarClinch Services a non-exclusive license to access your Artist Content through the StarClinch Services, and to use, reproduce, distribute, display and perform such Artist Content as permitted through the functionality of the StarClinch Services and under these Terms of Use. Notwithstanding the foregoing, you understand and agree that once Artist Content is incorporated into other aspects of the StarClinch Services by StarClinch and/or its members, StarClinch is under no obligation to delete or ask or require other StarClinch members to delete that Artist Content, and therefore, it may continue to appear and be used through the StarClinch Services indefinitely pursuant to an irrevocable, worldwide, royalty-free license. You understand and agree that we may retain, but will not display, distribute, or perform, server copies of Artist Content that have been removed or deleted.
              </p>
            </div>
          </motion.section>

          {/* Permitted Use of Content */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Permitted Use of Content
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The content associated with the StarClinch Services, including without limitation, the text, software, scripts, graphics, photographs, sounds, music, videos, visual interface, design, computer code, interactive features and the like (“Site Content”) and the trademarks, service marks, trade names and logos contained therein, except all Artist Content, are owned by or licensed to StarClinch, subject to copyright and other intellectual property rights under the law. Except as expressly and unambiguously provided herein, StarClinch does not grant you any express or implied rights, and all rights in and to the StarClinch Services and the Site Content are retained by StarClinch.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You may access Artist Content for your information and personal, non-commercial use solely as intended through the provided functionality of the StarClinch Services. Artist Content and Site Content are provided to you “AS IS” and “AS AVAILABLE” for your information and personal, non-commercial use only, to be used solely as intended through the provided functionality of the StarClinch Services and may not be downloaded, copied, reproduced, distributed, transmitted, broadcast, displayed, sold, licensed, or otherwise exploited for any other purposes whatsoever without the prior written consent of the respective owners, and it may not be used as prohibited under the Terms of Use.
              </p>
            </div>
          </motion.section>

          {/* Intellectual Property Rights */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Intellectual Property Rights
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Subject to aforesaid, it is hereby specifically recorded that the content available through the StarClinch Service is the sole and exclusive property of StarClinch or its licensors and is protected by copyright and other intellectual property laws. Intellectual Property Rights for the purpose of this Agreement shall always mean and include copyrights whether registered or not, patents including rights of filing patents, trade marks, trade names, trade dresses, collective marks, associate marks and the right to register them, designs both industrial and layout, geographical indicators, moral rights, broadcasting rights, displaying rights, distribution rights, selling rights, abridged rights, translating rights, reproducing rights, performing rights, communicating rights, adapting rights, circulating rights, protected rights, joint rights, reciprocating rights, infringement rights and all those special rights conferred as a result of the Berne Convention, 1979 individually as a result of prospective local laws in accordance with the Convention and the Trade Related Aspects of Intellectual Property Rights Agreement of the World Trade Organization. All those intellectual property rights arising as a result of domain names, internet or any other form of an electronic/technological law shall also vest in the domain of StarClinch. The parties hereto agree and confirm that no part of any intellectual property rights mentioned hereinabove is transferred in the name of Member and any intellectual property rights arising as a result of these presents shall also be in the absolute ownership, possession and control of StarClinch.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                KINDLY NOTE THAT STARCLINCH PROHIBITS PORNOGRAPHY ON ITS SITE. Please choose carefully the information you post on the Website and that you provide to other Members. Your StarClinch profile must not include any photographs containing nudity, or obscene, lewd, excessively violent, harassing, sexually explicit or otherwise objectionable subject matter. STRINGENT PENAL AND CRIMINAL ACTIONS WILL BE INITIATED AGAINST THOSE FOUND INDULGING IN ABOVE REFERRED ACTIVITIES. Any user or member who publishes or transmits or causes to be published in the electronic form, any material which is lascivious or appeals to the prurient interest or if its effect is such as to tend to deprave and corrupt people who are likely, having regard to all relevant circumstances, to read, see or hear the matter contained or embodied in it shall be strictly treated under the provision 67 of the Information Technology Act 2000 and as per the provisions of Indian Penal Code and other appropriate statutes applicable in that scenario. Despite this prohibition, information provided by other Members (for instance, in their profile) may contain inaccurate, inappropriate, offensive or sexually explicit material, products or services, and StarClinch assumes no responsibility or liability for this material. If you become aware of misuse of the services provided by StarClinch by any person, please contact StarClinch Web Admin.
              </p>
            </div>
          </motion.section>

          {/* Copyright Infringement Policy */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Copyright Infringement Policy
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                In accordance with the Indian Copyright Act and other applicable laws, we have adopted a policy of disabling or terminating, in appropriate circumstances and at our sole discretion, the account of any StarClinch member who infringes the intellectual property rights of you, us, or any third party. If you believe that your work has been copied in a way that constitutes copyright infringement, or your intellectual property rights have been otherwise violated, please notify us.
              </p>
            </div>
          </motion.section>

          {/* Member Accounts */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Member Accounts
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                In order to participate in the StarClinch Services, you must create an account and become a StarClinch Artist or StarClinch Client (collectively “Members“). All members will receive a password and are responsible for maintaining confidentiality of such password. Each member’s right to use the StarClinch Services is personal to the member and members are entirely responsible for any and all activities which occur under their account whether authorized or not authorized. You agree to notify StarClinch of any unauthorized use of your account or any other breach of security known or that should be known to you. You acknowledge and agree that you are solely responsible for your interaction with other members of the Site, and that we have no obligation to monitor or resolve disputes between members of the Site.
              </p>
              <p className="text-[15px] lg:text-base font-semibold text-white pt-1">
                You, as an Artist or Client, expressly agree:
              </p>
              <ul className="list-none space-y-2 pl-0">
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    to provide StarClinch with accurate, complete and updated registration information.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    that Company has the right to suspend and/or terminate your account and refuse any and all current or further use of the StarClinch Services if the Company determines, in its sole discretion, that any information you provided is untrue, inaccurate, not current and/or incomplete.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    to not approach, engage, solicit, make or accept any offers and/or otherwise deal with any each other directly or indirectly, without StarClinch’s involvement for any event undertaken by the Client.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    to protect your username and password at all times.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    to notify Company immediately of any unauthorized use of your account or any other need to deactivate your username and/or password due to security concerns.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    that Company shall not be responsible for unauthorized access to or alteration of your data.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    that any Content you submit or have submitted to Company in physical form (e.g. compact discs, DVDs, photographs, etc.) shall not be returned to you and shall become the exclusive property of Company, unless otherwise agreed to in writing by Company.
                  </span>
                </li>
              </ul>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Your username and password are personal to you and under no circumstances may you allow any others to use your username and/or password. We are not liable for any harm caused or related to the theft or misappropriation of your username and/or password, disclosure or your username and/or password, or your authorization of anyone else to use your username and/or password. From time to time, the Company may find it necessary to access your account. For instance, the Company may access your account for support, maintenance or security-related reasons. You acknowledge and consent to such access. Furthermore, recognizing the global nature of the Internet, you agree to comply with all local rules regarding online conduct and acceptable content. Specifically, you agree to comply with all applicable laws regarding the transmission of technical data exported from the country in which you reside and to comply with any other local laws affecting the transmission or posting of content or affecting the privacy of persons.
              </p>
            </div>
          </motion.section>

          {/* Member Conduct */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Member Conduct
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You may not use the StarClinch Services for any purpose that is unlawful or prohibited by the Terms of Use and/or any applicable additional terms, including the Rules, found. Your access to the StarClinch Services may be terminated immediately, in our sole discretion, with or without notice, if you fail to comply with any provision of the Terms of Use and/or additional terms, including the Rules, or for any other reason, or no reason. You expressly agree not to:
              </p>
              <ul className="list-none space-y-2 pl-0">
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Act as an agent, affiliate or representative of the StarClinch Services and utilize the StarClinch Services or Artist Content or Site Content for any commercial purpose other than for which the StarClinch Services is intended without our express written permission.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Access, monitor or copy any content or information of the StarClinch Services using any robot, spider, scraper or other automated means or any manual process for any purpose without our express written permission.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Take any action that imposes, or may impose, in our discretion, an unreasonable or disproportionate burden on our infrastructure.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    “Frame,” “mirror” or otherwise incorporate any part of the StarClinch Services into any other website without our prior written permission.
                  </span>
                </li>
              </ul>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You agree not to use the StarClinch Services for any commercial use, without the prior written authorization of StarClinch. Prohibited commercial uses include any of the following actions taken without our express approval:
              </p>
              <ul className="list-none space-y-2 pl-0">
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Sale of access to the StarClinch Services on another website.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Use of the StarClinch Services for the primary purpose of gaining advertising or subscription revenue.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Sale of advertising on the StarClinch Services or any third-party website, targeted specific Artist Content or Site Content.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Use of the StarClinch Services that we find, in our sole discretion, to use our resources or Artist Content with the effect of competing with or displacing the market for the StarClinch Services, Site Content, or Artist Content.
                  </span>
                </li>
              </ul>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Unauthorized commercial uses do not include uploading Artist Content to the StarClinch Services to promote your business or artistic enterprise, or any use that StarClinch expressly authorizes in writing.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                In addition, the following behaviours or activities are strictly prohibited when using the StarClinch Services by any member or StarClinch Services user:
              </p>
              <ul className="list-none space-y-2 pl-0">
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Strong, vulgar, obscene or otherwise harmful language.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Racially, ethnically or otherwise, objectionable language.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Harassing, intimidating, stalking or threatening other StarClinch members.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Libellous, defamatory or otherwise tortious language.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Online vandalism.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Impersonation of another person.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Posting, distributing, transmitting or promoting illegal Content.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Invasion of another’s privacy.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Actions that are hurtful to minors.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Posting, providing, transmitting or otherwise making available any materials or information infringing on the rights of a third party.
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Posting, providing, transmitting or otherwise making available any junk mail or spam, and
                  </span>
                </li>
                <li className="flex gap-3 text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8E8E93] shrink-0 mt-2" aria-hidden />
                  <span>
                    Posting, uploading, emailing or otherwise transmitting any material that contains any malicious computer code, or reverse engineering or hacking any materials associated with the StarClinch Services.
                  </span>
                </li>
              </ul>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                We maintain a policy of cooperation with any law enforcement authorities or court order requesting or directing StarClinch to disclose the identity of anyone posting any such information or materials. We have the right, but not an obligation, to strictly enforce the Terms of Use through self-help, moderation, active investigation, litigation and prosecution.
              </p>
            </div>
          </motion.section>

          {/* Fees and Terms */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Fees and Terms
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                When you register as an Artist / Client, from time to time, StarClinch allows creation of the profile and access to StarClinch Services at such charges as provided in their engagement terms contained in the Terms and Conditions for Artist and Clients prevailing at the time of registration. StarClinch promotes the Artist via their profiles as maintained by them on StarClinch.com to its Client registered with it, and thereby provide work to Artist (model, actor, actress, entertainer) through StarClinch.com, but there is no guarantee whatsoever to get any assignment. If any Client selects any Artist for performance in their respective service categories listed on the Website, StarClinch will contact such Artist thereabout and offer the assignment.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                We will charge 15% of the remuneration received by the Artist towards the assignments which the Artists get through us as our service fee. Notwithstanding anything contained herein StarClinch does not guarantee you any work for that matter.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The Client(s) and Artist(s) registered with StarClinch are prohibited to make any direct deal amongst themselves without the prior permission of StarClinch. In the event of any such engagement between the Artist and the Client where StarClinch has not been involved if found at later stage, each of the Artist and Client shall be liable to pay StarClinch a penalty of INR 750,000 which each party acknowledges as reasonable and justified compensation to StarClinch for breach of contract.
              </p>
            </div>
          </motion.section>

          {/* Termination of Service */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Termination of Service
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You agree that the Company, in its sole discretion, has the right, but not the obligation, to delete or deactivate your member account, block your IP address, or otherwise terminate your access to or use of the StarClinch Services, or any part thereof, immediately and without notice, and remove and delete any Artist Content on the StarClinch Services, for any reason, including, without limitation, if we believe that you have violated or acted inconsistently with the letter or spirit of the Terms of Use. Further, you agree that StarClinch will not be liable to you or any third party for any termination of your access to the StarClinch Services. Further, you agree not to attempt to use the StarClinch Services after your member account has been terminated.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You agree that if you are dissatisfied with the StarClinch Services, if you do not agree with any part of the Terms of Use, or if you have any other dispute or claim with or against us with respect to the Terms of Use or the StarClinch Services, your sole and exclusive remedy is to discontinue using the StarClinch Services.
              </p>
            </div>
          </motion.section>

          {/* Links */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Links
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The StarClinch Services may provide links to other websites or resources. Because we have no control over such websites and resources, you acknowledge and agree that StarClinch is not responsible for the availability of such external websites and resources, and does not endorse and is not responsible or liable for any content, advertising, products or other materials on or available from such websites or resources. You further acknowledge and agree that we will not be liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use or reliance on any such content, goods or services available on or through any such website or resource.
              </p>
            </div>
          </motion.section>

          {/* Non-Circumvention / No-Direct-Dealing */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Non-Circumvention / No-Direct-Dealing
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The Client(s) and Artist(s) registered with StarClinch are prohibited to make any direct deal amongst themselves without the prior permission of StarClinch.
              </p>
              <p className="text-[15px] lg:text-base font-semibold text-white pt-1">
                Express Booking Clause
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The Non-Circumvention clause applies strictly and perpetually to Express Booking assignments. Any subsequent or follow-up engagements with a Client introduced via Express Booking must be booked exclusively through StarClinch.
              </p>
            </div>
          </motion.section>

          {/* Children / Minor Policy */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Children / Minor Policy
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                This website is recommended for use of individuals of age of 18 years or above, but if you are above 13 years of age or older but below 18 years, you may be granted access by providing either of their parents Email IDs and an Email will be sent informing the parent that their ward has registered himself/herself to use the StarClinch Services through this website. Parents of such wards will be liable for damages or losses occurred due to activities or action of their wards and are legally bound by any Acts/ Deeds/ Violations of the StarClinch Terms of Use by their wards. StarClinch shall not be liable for a minor viewing the website without a parent or guardian.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Express Booking features are restricted to users who are 18 years of age or older. Verification may be requested.
              </p>
            </div>
          </motion.section>

          {/* Indemnity */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Indemnity
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You agree to defend, indemnify and hold StarClinch, our owner, and each of its subsidiaries, affiliates, directors, officers, agents, vendors or other partners, employees and StarClinch moderators harmless from any claim or demand, including attorneys’ fees and costs, made by any third party due to or arising out of any Content, material or information posted, provided, transmitted or otherwise made available by you on or through the StarClinch Services, by your violation of the Terms of Use, or by your violation of any third party right, including without limitation any copyright, property, or privacy right.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You agree to defend, indemnify and hold StarClinch, our owner, and each of its subsidiaries, affiliates, directors, officers, agents, vendors or other partners, employees and StarClinch moderators harmless from any claim or demand, including attorneys’ fees and costs, made by any third party due to or arising out of any Content, material or information posted, provided, transmitted or otherwise made available by you on or through the StarClinch Services, by your violation of the Terms of Use, or by your violation of any third party right, including without limitation any copyright, property, or privacy right.
              </p>
            </div>
          </motion.section>

          {/* Trademark Information */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Trademark Information
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                StarClinch is a registered trademark and logo. You agree that all StarClinch’s trademarks, including StarClinch trade names, service marks, logos and service names are trademarks and the property of StarClinch. Without our prior permission, you agree not to display or use in any manner any StarClinch mark. All trademarks included in the StarClinch Services not owned by StarClinch are the property of their respective owners. Nothing contained on the StarClinch Services may be construed as granting, by implication, estoppel, or otherwise, any right or license to use any trademark.
              </p>
            </div>
          </motion.section>

          {/* Disclaimer of Warranties / Limitation of Liability */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Disclaimer of Warranties / Limitation of Liability
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You understand and agree that the StarClinch Services are provided “AS-IS” or “AS AVAILABLE,” and that we assume no responsibility for your ability to (or any costs or fees associated with your ability to) obtain access to the Model Mayhem Services, nor do we assume any liability for the failure to store or maintain any user communications or personal settings.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                No advice or information, whether oral or written, obtained by you from the StarClinch Services shall create any warranty not expressly stated in these Terms of Use. In no event shall StarClinch or its owner, or affiliates, be liable to you or any third party for any indirect, consequential, exemplary, incidental, special or punitive damages, including lost profit damages, loss of or damage to reputation or loss of information, arising from your use of the StarClinch Services even if we have been advised of the possibility of such damages.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Some jurisdictions do not allow the disclaimer, exclusion or limitation of incidental or consequential damages, so the foregoing disclaimer, exclusion and limitation may not apply to you, and you may have other legal rights that vary according to jurisdiction. In no event will damages provided by law, if any, apply unless they are required to apply by law, notwithstanding their exclusion by contract.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                We disclaim all liability for any loss or damage arising out of your communications or dealings with any of the businesses, advertisers, StarClinch members or users of the StarClinch Services. Your communications or dealings with such businesses, advertisers, members and users are solely between you and them, although we reserve the right to monitor disputes between you and them.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                If you promote a contest, sweepstakes or giveaway on StarClinch without our express, advanced approval, you do so at your risk and you assume all risk and liability. You further agree to hold StarClinch harmless from any claim, demand or damages.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                StarClinch shall not be liable for your interactions with any organizations and/or individuals found on or through the StarClinch Services. This includes but is not limited to performance of services by the Artists, payment by Client, and any other terms, conditions, warranties or representations associated with such dealings. These dealings are solely between you and such organizations and/or individuals. You agree that StarClinch is not responsible for any damage or loss incurred as a result of any such dealings.
              </p>
            </div>
          </motion.section>

          {/* General Provisions */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                General Provisions
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                If there is any claim or dispute about or involving the StarClinch Services, you agree that the claim or dispute will be governed by the laws of India, without regard to conflict of law provisions. You agree to and hereby submit to exclusive personal jurisdiction and venue in the courts located in the Delhi, India with respect to any such matters, and you agree to waive any and all objections to jurisdiction and to venue. The StarClinch Services are controlled and offered by StarClinch in the India and we make no representation that materials associated with the StarClinch Services are appropriate or available for use in other locations, and accessing them from jurisdictions where the content is illegal is prohibited. Those who choose to use or access the StarClinch Services from other jurisdictions do so at their own initiative and are responsible for compliance with local laws.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                You affirm that you are either more than 18 years of age, or an emancipated minor, or possess legal parental or guardian consent, and are fully able and competent to enter into these terms, conditions, obligations, affirmations, representations, and warranties set forth in the Terms of Use, and to abide by and comply with the Terms of Use.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Our failure to exercise or enforce any right or provision of the Terms of Use will not operate as a waiver of such right or provision. If any provision of the Terms of Use is unlawful, void or unenforceable, that provision is deemed severable and does not affect the validity and enforceability of any remaining provisions.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                If any provision of this Agreement is invalid or unenforceable under applicable law, it is, to that extent, deemed omitted and the remaining provisions will continue in full force and effect. This Agreement will bind and inure to the benefit of each party’s permitted successors and assigns. This Agreement is personal to you and may not be transferred, assigned or delegated to anyone. Any attempt by you to assign, transfer or delegate this Agreement shall be null and void. This Agreement (including all documents expressly incorporated herein by reference, including but not limited to the Starclinch engagement terms with its Artist and Client, as applicable) constitutes the complete and exclusive agreement between StarClinch and you with respect to the subject matter hereof and supersedes all prior oral or written understandings, communications or agreements not specifically incorporated herein.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                The Terms herein are subject to prevailing laws and legal process, and nothing contained in this Agreement is in derogation of our right to comply with statutory requests or requirements relating to your use of the StarClinch Service or information provided to or gathered by us with respect to such use.
              </p>
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                This Agreement or any other communication in electronic form or printed shall be admissible in judicial or arbitration proceedings as if produced in printed form.
              </p>
            </div>
          </motion.section>

          {/* YouTube Terms of Service */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                YouTube Terms of Service
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Starclinch.com utilizes YouTube API Services to enhance user experience and provide content integration. By using our services, you agree to be bound by YouTube's Terms of Service. It is important to review and familiarize yourself with the YouTube Terms of Service, which can be found at [YouTube Terms of Service].
              </p>
            </div>
          </motion.section>

          {/* Use of YouTube API Services */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Use of YouTube API Services
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                Starclinch.com uses YouTube API Services to provide certain functionalities and features. You acknowledge and agree that your use of our services involves the collection, storage, and processing of data through the YouTube API.
              </p>
            </div>
          </motion.section>

          {/* Google Privacy Policy */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: SECTION_STAGGER * 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
          >
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-1">
              <h2 className="text-base font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#F33363] shrink-0" aria-hidden />
                Google Privacy Policy
              </h2>
            </div>
            <div className="px-5 pb-5 lg:px-6 lg:pb-6 pt-2 space-y-4">
              <p className="text-[#E5E5EA] text-[15px] lg:text-base leading-[1.6]">
                In addition to our Privacy Policy, we want to inform you that Starclinch.com is subject to Google's Privacy Policy. You can review Google's Privacy Policy at https://www.google.com/policies/privacy. We encourage you to read and understand how Google handles your information.
              </p>
            </div>
          </motion.section>
        </div>


        {/* Footer hint – iOS-style */}
        <p className="mt-8 text-center text-xs text-[#8E8E93]">
          © Copyright {new Date().getFullYear()} | StarClinch | All Rights Reserved.
        </p>
      </div >
    </div >
  );
};
